import {
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    effect,
    inject,
    input,
    output,
    signal,
    viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { map } from 'rxjs';

import { DeviceData, DeviceSize } from '../../../_shared/data/device.data';
import { LinkDirective } from '../../../_shared/directives/link.directive';
import { TrackingEvents } from '../../../_shared/data/tracking-events.data';
import { ButtonSize } from '../../../_shared/types/button/button-size.type';
import { IconName } from '../../../_shared/models/icon.model';
import { ILink } from '../../../_shared/models/link.model';
import { IMenuCart, IMenuGroup } from '../../../_shared/models/navigation/menu/menu-group.model';
import { GtmService } from '../../../_shared/services/gtm.service';
import { LinkService } from '../../../_shared/services/link.service';
import { SnowplowService } from '../../../_shared/services/snowplow.service';
import { ICustomWindow, WindowRefService } from '../../../_shared/services/window-ref.service';
import { BadgeColor, BadgeComponent, BadgeSize } from '../../../badge/badge.component';
import { ButtonComponent } from '../../../button/button.component';
import { ProfileMenuComponent } from '../../../profile-menu/profile-menu.component';
import { SearchComponent } from '../../../search/search.component';
import { NavigationService } from '../../navigation.service';

@Component({
    selector: 'sirius-navigation-menu-desktop',
    templateUrl: './navigation-menu-desktop.component.html',
    styleUrls: ['./navigation-menu-desktop.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(window:keydown.enter)': 'onEnterPressed()',
        '(focusout)': 'onBlur($event)',
        '(window:scroll)': 'onScroll()',
    },
    imports: [LinkDirective, ProfileMenuComponent, SearchComponent, ButtonComponent, BadgeComponent, TranslateModule],
})
export class NavigationMenuDesktopComponent {
    private readonly _navService = inject(NavigationService);
    private readonly _router = inject(Router);
    private readonly _gtmService = inject(GtmService);
    private readonly _snowplowService = inject(SnowplowService);
    private readonly _linkService = inject(LinkService);
    private readonly _windowRef = inject(WindowRefService);
    private readonly _breakpointObserver = inject(BreakpointObserver);

    readonly ctaButtonSize = toSignal(
        this._breakpointObserver
            .observe(`(min-width: ${DeviceData.breakpoints[DeviceSize.XL]}px)`)
            .pipe(map((state) => (state.matches ? 'medium' : 'small') as ButtonSize)),
        { initialValue: 'small' as ButtonSize }
    );

    readonly menuGroups = input<IMenuGroup[]>([]);
    readonly siteRoot = input.required<ILink>();
    readonly searchPageUrl = input('');
    readonly logos = input.required<string[]>();
    readonly profileEnabled = input.required<boolean>();
    readonly cartData = input<IMenuCart | undefined>(undefined);
    readonly ctaButton = input<ILink | undefined>(undefined);
    readonly profileMenuOptions = input<{ icon: IconName; link: ILink }[] | undefined>(undefined);
    readonly activeMenuIndex = input<number | null>(null);

    readonly profileClicked = output<MouseEvent>();
    readonly menuItemClicked = output<number>();
    readonly menuItemEntered = output<number>();
    readonly mobileMenuToggle = output<boolean>();

    private readonly _navListRef = viewChild.required<ElementRef<HTMLElement>>('navList');
    private readonly _searchContainerRef = viewChild<ElementRef<HTMLElement>>('searchContainer');
    private readonly _searchComponent = viewChild<SearchComponent>(SearchComponent);

    readonly isSearchBarOpen = signal(false);

    readonly badgeColor = BadgeColor;
    readonly badgeSize = BadgeSize;

    private _timer?: ReturnType<typeof setTimeout>;

    constructor() {
        // Focus the search input when the bar opens. When search is not rendered
        // (no searchPageUrl), the viewChild signal stays undefined and we return
        // early instead of accessing a missing SearchComponent instance.
        effect(() => {
            const searchComponent = this._searchComponent();
            if (!searchComponent) return;

            if (this.isSearchBarOpen()) {
                searchComponent.input.nativeElement.focus();
            }
        });
    }

    private get window(): ICustomWindow | undefined {
        return this._windowRef.NativeWindow;
    }

    onEnterPressed(): void {
        const searchComponent = this._searchComponent();
        if (!searchComponent) return;

        const input = searchComponent.input.nativeElement;
        const query: string = searchComponent.control.value;

        searchComponent.control.setValue('');
        this.isSearchBarOpen.set(false);
        input.blur();

        if (query) {
            this.submit(query);
        }
    }

    onBlur(event: FocusEvent): void {
        const container = this._searchContainerRef()?.nativeElement;
        if (!container) return;

        const relTarget = event.relatedTarget as HTMLElement | null;
        if (!container.contains(relTarget)) {
            this.isSearchBarOpen.set(false);
        }
    }

    onScroll(): void {
        this.isSearchBarOpen.set(false);
    }

    onMouseEnter(index: number, event: MouseEvent): void {
        const target = event.currentTarget as HTMLElement;
        event.preventDefault();

        if (this.isMenuItem(target) && !this._navService.wasClicked) {
            this._timer = setTimeout(() => this.menuItemEntered.emit(index), 250);
            this.isSearchBarOpen.set(false);
        }
    }

    onMouseLeave(): void {
        if (this._timer) {
            clearTimeout(this._timer);
        }
        this._navService.wasClicked = false;
    }

    onMenuItemClicked(link: ILink, event: Event, index?: number): void {
        this.addTracking(link, event.target as HTMLElement);

        if (index === undefined) return;

        const target = event.currentTarget as HTMLElement;
        if (!this.isMenuItem(target)) return;

        if (this._timer) {
            clearTimeout(this._timer);
        }

        this.menuItemClicked.emit(index);
        this.isSearchBarOpen.set(false);
        this._navService.wasClicked = true;
    }

    onMenuIconClicked(): void {
        this.isSearchBarOpen.set(false);
        this.mobileMenuToggle.emit(true);
    }

    onSearchIconClicked(value: boolean): void {
        this.isSearchBarOpen.set(value);
    }

    onSearchCancelClicked(value: boolean): void {
        this.isSearchBarOpen.set(value);
    }

    onProfileMenuClicked($event: MouseEvent): void {
        this.profileClicked.emit($event);
    }

    hasSubmenu(menuGroup: IMenuGroup): boolean {
        return this._navService.hasItems(menuGroup);
    }

    isSubmenuOpen(index: number): boolean {
        return this.activeMenuIndex() === index;
    }

    private submit(query: string): void {
        if (query) {
            this._router.navigate([this.searchPageUrl()], {
                queryParams: { query: query },
            });
        }
    }

    private isMenuItem(target: HTMLElement): boolean {
        return this._navListRef().nativeElement.contains(target);
    }

    //#region tracking
    private addTracking(link: ILink, anchor: HTMLElement): void {
        if (!this.window) return;

        const data = this._linkService.getTrackingData(anchor);
        const cfg = {
            ...link.tracking?.parameters,
            link_classes: data.linkClasses,
            link_id: data.linkId,
            link_text: data.linkText,
            link_url: data.linkUrl,
            outbound: data.outbound,
        };

        this._gtmService.track(TrackingEvents.gtm['topMenuClick']([cfg]));

        const path = this._linkService.normalizeHref(link.href) ?? '';
        const targetUrl = `${this.window.location.origin}${path}`;
        const cfg2 = {
            identifier: link.tracking?.parameters['identifier'],
            category: 'menu',
            element_classes: data.linkClasses.split(' '),
            target_url: targetUrl,
            element_content: data.linkText,
            outbound: data.outbound !== 'FALSE',
            context: [],
        };

        this._snowplowService.track('linkClick', cfg2);
    }
    //#endregion
}
